import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState,
} from "react";
import { Button, Input, Modal, Form, Image, Switch } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../reducers";
import { ReduxActionTypes } from "../../../../constants/ReduxActionConstants";

const DEFAULT_ICON =
  "https://ubos.tech/wp-content/uploads/2024/07/cropped-UBOS-Logo-Small.png";

export const IframeModal = forwardRef((_, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [form] = Form.useForm();
  const [isFormValid, setIsFormValid] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const dispatch = useDispatch();
  const currentApplication = useSelector(
    (state: AppState) => state.ui.applications.currentApplication,
  );

  const toggleOverlay = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const createIframe = useCallback(() => {
    const { name, url, icon, isDefaultQueryParams } = form.getFieldsValue();

    if (!name || !url) return;

    let iframeObject = {
      id: "nodeRedId",
      title: name,
      component: "Iframe",
      props: url,
      icon: icon || DEFAULT_ICON,
    };
    if (isDefaultQueryParams) {
      iframeObject = {
        id: "nodeRedId",
        title: name,
        component: "nodeRed",
        props: url,
        icon: icon || DEFAULT_ICON,
      };
    }

    if (currentApplication?.global_setting?.iframe) {
      dispatch({
        type: ReduxActionTypes.UPDATE_GLOBAL_APPLICATION_SETTINGS,
        payload: {
          id: currentApplication?.id,
          name: "iframe",
          value: [...currentApplication?.global_setting.iframe, iframeObject],
        },
      });
    }

    dispatch({
      type: ReduxActionTypes.SET_LAYOUT_ITEM,
      payload: iframeObject,
    });

    form.resetFields();
    setIsFormValid(false);
    setNameError(null);
    toggleOverlay();
  }, [dispatch, form, currentApplication]);

  useImperativeHandle(ref, () => ({
    openModal() {
      form.resetFields();
      setIsFormValid(false);
      setNameError(null);
      setIsOpen(true);
    },
  }));

  const handleFormChange = () => {
    const { name, url } = form.getFieldsValue();
    const trimmedName = name?.trim();

    const existing = currentApplication?.global_setting?.iframe?.some(
      (iframe: any) => iframe.title.trim() === trimmedName,
    );

    if (trimmedName === "AI Chatbot") {
      setNameError("Name 'AI Chatbot' is reserved and cannot be used.");
      setIsFormValid(false);
    } else if (existing) {
      setNameError("This name is already taken");
      setIsFormValid(false);
    } else {
      setNameError(null);
      setIsFormValid(!!trimmedName && !!url);
    }
  };

  const iconUrl = Form.useWatch("icon", form) || DEFAULT_ICON;

  return (
    <Modal
      title="New Iframe"
      open={isOpen}
      onCancel={toggleOverlay}
      footer={null}
      centered
    >
      <Form
        form={form}
        layout="vertical"
        onFieldsChange={handleFormChange}
        initialValues={{ isVisible: true, isDefaultQueryParams: true }}
      >
        <Form.Item
          label="Iframe Name"
          name="name"
          validateStatus={nameError ? "error" : ""}
          help={nameError || ""}
          rules={[{ required: true, message: "Please enter iframe name" }]}
        >
          <Input placeholder="Enter iframe name" />
        </Form.Item>

        <Form.Item
          label="Iframe URL"
          name="url"
          rules={[{ required: true, message: "Please enter iframe URL" }]}
        >
          <Input placeholder="Enter iframe URL" />
        </Form.Item>

        <Form.Item label="Icon URL" name="icon">
          <Input placeholder="Enter optional icon URL" />
        </Form.Item>

        <Form.Item label="Preview Icon">
          <Image
            src={iconUrl}
            alt="Icon Preview"
            width={40}
            height={40}
            preview={false}
            fallback={DEFAULT_ICON}
          />
        </Form.Item>

        <Form.Item
          label="Add default query params"
          name="isDefaultQueryParams"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item style={{ textAlign: "right" }}>
          <Button type="primary" onClick={createIframe} disabled={!isFormValid}>
            Create Iframe
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
});

IframeModal.displayName = "IframeModal";
